import * as React from 'react';
import {
    styled,
    Switch as MuiSwitch,
    type SwitchProps as MuiSwitchProps,
    PaletteColor,
} from '@mui/material';
import './Switch.css';
import { FaOptionIcon, checkIcon, errorIcon } from '@europrocurement/l2d-icons';
import { Theme } from '@mui/system';

export type NativeSwitchProps = MuiSwitchProps;

export type SwitchProps = Omit<NativeSwitchProps, 'value'> & {
    value?: boolean;
    defaultValue?: boolean;
    color?: PaletteColor;
};

export type SwitchStyleProps = {
    theme: Theme;
    color: PaletteColor;
};

/**
 * Switch component :
 * Checked value priority : Value > Default value > false
 */
export const Switch: React.FunctionComponent<SwitchProps> = styled(({ ...props }: SwitchProps) => {
    const { defaultValue = false, value, onChange, ...otherProps } = props;

    const isControlled = typeof value !== 'undefined';

    const switchProps = isControlled
        ? {
              checked: !!value,
              onChange,
          }
        : {
              defaultChecked: !!defaultValue,
              onChange,
          };

    const checkedIcon = <FaOptionIcon {...checkIcon.props} />;
    const icon = <FaOptionIcon {...errorIcon.props} />;

    return (
        <MuiSwitch
            {...otherProps}
            {...switchProps}
            role="switch"
            icon={icon}
            checkedIcon={checkedIcon}
        />
    );
})(({ theme, color = theme.palette.primary }: SwitchStyleProps) => ({
    '& .MuiSwitch-switchBase': {
        backgroundColor: theme.palette.grey[200],
        '&:before': {
            backgroundColor: '#fff',
        },
        '&:hover': {
            backgroundColor: theme.palette.grey[200],
        },
        '.MuiTypography-root': {
            color: theme.palette.grey[500],
        },
        '&.Mui-checked': {
            backgroundColor: color.light,
            '&:before': {
                backgroundColor: color.dark,
            },
            '&:hover': {
                backgroundColor: color.light,
            },
            '.MuiTypography-root': {
                color: color.dark,
            },
        },
        '&.Mui-disabled': {
            backgroundColor: theme.palette.grey[100],
            '&:before': {
                backgroundColor: theme.palette.grey[100],
            },
            '&.Mui-checked': {
                backgroundColor: color.light,
                opacity: 0.5,
                '&:before': {
                    backgroundColor: color.dark,
                },
            },
        },
    },
}));
